import gql from 'graphql-tag';

const APPLICATION_FRAGMENT = gql`
  fragment ApplicationDetails on Application {
    id
    amount
    taxAmount
    baseAmount
    chargesAmount
    status {
      name
    }
    fullAmount
    loanDuration
    dateOfRepayment
    requiredSteps
    completedSteps
    createdAt
  }
`;

export const CREATE_CUSTOMER = gql`
  mutation (
    $bvn: String!
    $dateOfBirth: DateTime
    $firstName: String!
    $lastName: String!
    $email: String!
    $phone: String!
    $password: String!
    $maritalStatus: MaritalStatus
    $clientId: ID!
    $gender: Gender
    $residentialAddress: String
    $residentialState: String
    $localGovernment: String
  ) {
    createCustomer(
      input: {
        bvn: $bvn
        dateOfBirth: $dateOfBirth
        firstName: $firstName
        lastName: $lastName
        email: $email
        phone: $phone
        password: $password
        maritalStatus: $maritalStatus
        clientId: $clientId
        gender: $gender
        address: $residentialAddress
        state: $residentialState
        localGovernment: $localGovernment
      }
    ) {
      token
      account {
        id
      }
      user {
        id
        bvnStatus {
          isVerified
          dateOfBirth
          isMatchingPhone
        }
      }
    }
  }
`;

export const CONFIRM_PHONE = gql`
  mutation ($code: String!, $userId: ID!) {
    confirmPhone(input: { code: $code, userId: $userId }) {
      ok
    }
  }
`;

export const TRIGGER_PHONE_CONFIRMATION = gql`
  mutation ($userId: ID!) {
    triggerPhoneConfirmation(input: { userId: $userId }) {
      ok
    }
  }
`;

export const TRIGGER_BVN_CONFIRMATION = gql`
  mutation ($userId: ID!, $bvnPhoneDigits: String!) {
    triggerBvnPhoneConfirmation(
      input: { userId: $userId, bvnPhoneDigits: $bvnPhoneDigits }
    ) {
      ok
    }
  }
`;

export const UPDATE_PHONE_NUMBER = gql`
  mutation ($userId: ID!, $phone: String!) {
    updatePhoneNumber(input: { userId: $userId, phone: $phone }) {
      ok
      record {
        isPhoneConfirmed
        phone
      }
    }
  }
`;

export const UPDATE_USER_DETAILS = gql`
  mutation (
    $address: String
    $state: String
    $localGovernment: String
    $dateMovedIn: DateTime
    $employmentStatus: String
    $employerName: String
    $workAddress: String
    $jobRole: String
    $workEmail: String
    $workStartDate: DateTime
    $netIncome: String
    $workId: String
    $selfie: String
    $refereeName: String
    $refereePhone: String
    $refereeWorkPlace: String
    $refereeEmail: String
    $facebookProfileUrl: String
    $socialMediaInformation: [SocialMediaInput]
  ) {
    updateUserData(
      input: {
        address: $address
        state: $state
        localGovernment: $localGovernment
        dateMovedIn: $dateMovedIn
        employmentStatus: $employmentStatus
        employerName: $employerName
        workAddress: $workAddress
        jobRole: $jobRole
        workEmail: $workEmail
        workStartDate: $workStartDate
        netIncome: $netIncome
        workId: $workId
        selfie: $selfie
        refereeName: $refereeName
        refereePhone: $refereePhone
        refereeWorkPlace: $refereeWorkPlace
        refereeEmail: $refereeEmail
        facebookProfileUrl: $facebookProfileUrl
        socialMediaInformation: $socialMediaInformation
      }
    ) {
      success
    }
  }
`;

export const AUTHENTICATE_CUSTOMER_FACEBOOK = gql`
  mutation ($accessToken: String!) {
    authenticateCustomerFacebook(input: { accessToken: $accessToken }) {
      ok
    }
  }
`;

export const TRIGGER_WORK_EMAIL_CONFIRMATION = gql`
  mutation ($clientId: ID!, $workEmail: String!) {
    triggerWorkEmailConfirmation(
      input: { clientId: $clientId, workEmail: $workEmail }
    ) {
      ok
    }
  }
`;

export const CONFIRM_WORK_EMAIL = gql`
  mutation ($code: String!, $workEmail: String!) {
    confirmWorkEmail(input: { code: $code, workEmail: $workEmail }) {
      ok
    }
  }
`;

export const TRIGGER_PERSONAL_EMAIL_CONFIRMATION = gql`
  mutation triggerPersonalEmailConfirmation {
    triggerPersonalEmailConfirmation {
      ok
    }
  }
`;

export const UPDATE_USER_EMAIL = gql`
  mutation updateUserEmail($userId: ID!, $email: String!) {
    updateUserEmail(input: { userId: $userId, email: $email }) {
      ok
      record {
        isEmailConfirmed
      }
    }
  }
`;

export const CONFIRM_PERSONAL_EMAIL = gql`
  mutation confirmPersonalEmail($code: String!) {
    confirmPersonalEmail(code: $code) {
      ok
    }
  }
`;
export const BANK_STATEMENT_REQUEST = gql`
  mutation (
    $applicationId: ID!
    $ticketNum: String
    $password: String
    $skipStep: Boolean
  ) {
    completeBankStatementRequest(
      input: {
        applicationId: $applicationId
        ticketNum: $ticketNum
        password: $password
        skipStep: $skipStep
      }
    ) {
      success
    }
  }
`;

export const APPLICATION_CREDIT_SCORE = gql`
  mutation ($applicationId: ID!) {
    getApplicantCreditScore(input: { applicationId: $applicationId }) {
      success
      application {
        credit {
          score
        }
        status {
          name
        }
      }
    }
  }
`;

export const EXTERNAL_BANK_STATEMENT_REQUEST = gql`
  mutation (
    $applicationId: ID!
    $ticketNum: String!
    $password: String!
    $skipStep: Boolean!
  ) {
    completeExternalBankStatementRequest(
      input: {
        applicationId: $applicationId
        ticketNum: $ticketNum
        password: $password
        skipStep: $skipStep
      }
    ) {
      success
      application {
        requiredSteps
        completedSteps
      }
    }
  }
`;

export const INITIATE_APPLICATIONS = gql`
  mutation (
    $clientId: ID!
    $amount: Float!
    $loanDuration: Int!
    $dateOfRepayment: DateTime!
    $dayOfRepayment: Int
    $durationType: DurationType
    $loanCategoryId: ID
    $source: Source
  ) {
    initiateApplication(
      input: {
        clientId: $clientId
        amount: $amount
        loanDuration: $loanDuration
        dateOfRepayment: $dateOfRepayment
        dayOfRepayment: $dayOfRepayment
        durationType: $durationType
        loanCategoryId: $loanCategoryId
        source: $source
      }
    ) {
      success
      denied
      application {
        ...ApplicationDetails
      }
      errors {
        message
      }
      warnings {
        message
      }
    }
  }
  ${APPLICATION_FRAGMENT}
`;

export const SET_APPLICATION_BANKACCOUNT = gql`
  mutation (
    $applicationId: ID!
    $accountBankId: ID!
    $skippedAutomaticBankStatement: BankStatementSkipMetaEnum
  ) {
    setApplicationBankAccount(
      input: {
        applicationId: $applicationId
        accountBankId: $accountBankId
        skippedAutomaticBankStatement: $skippedAutomaticBankStatement
      }
    ) {
      success
      application {
        id
        amount
        taxAmount
        baseAmount
        chargesAmount
        fullAmount
        loanDuration
        dateOfRepayment
        requiredSteps
        completedSteps
      }
    }
  }
`;

export const INITIATE_BANK_STATEMENT_REQUEST = gql`
  mutation ($applicationId: ID!, $accountBankId: ID!, $isRetried: Boolean) {
    initiateBankStatementRequest(
      input: {
        applicationId: $applicationId
        accountBankId: $accountBankId
        isRetried: $isRetried
      }
    ) {
      success
    }
  }
`;

export const ADD_BANK_ACCOUNT = gql`
  mutation (
    $bankId: ID!
    $accountName: String!
    $accountNumber: String!
    $okraCustomerId: String
    $okraRecordId: String
    $okraAccountId: String
    $directDebitAuthorised: Boolean
  ) {
    addAccountBank(
      input: {
        bankId: $bankId
        accountName: $accountName
        accountNumber: $accountNumber
        okraCustomerId: $okraCustomerId
        okraRecordId: $okraRecordId
        okraAccountId: $okraAccountId
        directDebitAuthorised: $directDebitAuthorised
      }
    ) {
      id
      status
      isDefault
    }
  }
`;

export const COMPLETE_APPLICATION = gql`
  mutation ($applicationId: ID!) {
    completeApplication(input: { applicationId: $applicationId }) {
      approved
      errors {
        message
      }
      warnings {
        message
      }
      portfolio {
        id
      }
      application {
        ...ApplicationDetails
      }
    }
  }
  ${APPLICATION_FRAGMENT}
`;

export const GENERATE_APPLICATION_OFFERS = gql`
  mutation ($applicationId: ID!) {
    generateApplicationOffers(input: { applicationId: $applicationId }) {
      application {
        ...ApplicationDetails
      }
      suggestedOffer {
        id
        data {
          fullAmount
          baseAmount
          noOfRepayments
          chargesAmount
          repaymentBreakdown {
            principalBalance
            expectedPayment
            interestPortion
            principalPortion
            endingBalance
            dueDate
          }
        }
        policy {
          id
          processingFee {
            value
            name
            type
            calcBy
          }
          interestRate {
            value
          }
        }
      }
    }
  }
  ${APPLICATION_FRAGMENT}
`;

export const CREATE_CONTEXT_STATE = gql`
  mutation createContextState(
    $state: JSON
    $context: ContextType!
    $page: String
  ) {
    createContextState(
      input: { state: $state, context: $context, page: $page }
    ) {
      id
      page
      state
    }
  }
`;

export const UPDATE_CONTEXT_STATE = gql`
  mutation updateContextState($id: ID!, $state: JSON, $page: String) {
    updateContextState(input: { id: $id, state: $state, page: $page }) {
      id
      state
      page
    }
  }
`;
